import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { moduleIcon } from "@/lib/moduleIcons";

/**
 * Pair of side-by-side CTA cards used as the empty state on Dashboards and
 * Data pages. Teaches the user the two paths into the platform:
 *   1. Connect data → prebuilt dashboards auto-instantiate
 *   2. Ask Sense AI Builder → custom dashboard generated in a single turn
 *
 * Card copy + links are keyed by the `context` prop ("dashboards" | "data")
 * so the same component renders the inverse pair on each page.
 *
 * No hardcoded colours — uses `border-border`, `bg-card`, `text-primary` so
 * the theme provider drives appearance.
 */
export default function EmptyStateCTAs({ context = "dashboards", "data-testid": testId }) {
    const DataIcon    = moduleIcon("data");
    const SenseAiIcon = moduleIcon("sense_ai");
    const cards = context === "dashboards"
        ? [
            {
                icon: DataIcon,
                eyebrow: "Path 1",
                title: "Connect data → prebuilts auto-load",
                body: "Wire up Google Ads, Meta, GA4, your warehouse, or upload a CSV. Prebuilt dashboards instantiate automatically the moment the canonical models they bind to have rows.",
                cta: "Connect a source",
                href: "/data?tab=connectors&add_source=1",
                tid: "empty-cta-connect-data",
            },
            {
                icon: SenseAiIcon,
                eyebrow: "Path 2",
                title: "Ask Sense AI Builder for a custom dashboard",
                body: "Describe what you want to measure. Sense AI Builder picks the data model, drafts widgets, and saves the dashboard — all in one turn.",
                cta: "Open Sense AI Builder",
                href: "/sense-ai?mode=builder",
                tid: "empty-cta-builder",
            },
        ]
        : [
            {
                icon: DataIcon,
                eyebrow: "Path 1",
                title: "Connect a live source",
                body: "Pick a connector (Google Ads, Meta, GA4, Postgres, MySQL, MongoDB) and run a sync. Models populate automatically — and any prebuilt dashboard bound to those models lights up.",
                cta: "Browse connectors",
                href: "/data?tab=connectors&add_source=1",
                tid: "empty-cta-connectors",
            },
            {
                icon: SenseAiIcon,
                eyebrow: "Path 2",
                title: "Or skip ahead with Sense AI Builder",
                body: "If you already have a dashboard goal in mind, Sense AI Builder will tell you which models you need and what fields are missing — saving you an exploration loop.",
                cta: "Open Sense AI Builder",
                href: "/sense-ai?mode=builder",
                tid: "empty-cta-builder",
            },
        ];

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 py-8" data-testid={testId || "empty-state-ctas"}>
            {cards.map((c) => (
                <Link key={c.cta} to={c.href}
                      className="border border-border rounded-sm bg-card p-6 hover:border-primary/60 hover:shadow-sm transition-all group"
                      data-testid={c.tid}>
                    <div className="flex items-start gap-4">
                        <div className="border border-border rounded-sm p-2 bg-secondary/30 shrink-0">
                            <c.icon className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-mono uppercase tracking-wider opacity-70 mb-1">{c.eyebrow}</div>
                            <div className="text-sm font-medium mb-2">{c.title}</div>
                            <div className="text-xs text-muted-foreground leading-relaxed mb-4">{c.body}</div>
                            <div className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-primary group-hover:gap-2 transition-all">
                                {c.cta} <ArrowRight className="h-3 w-3" />
                            </div>
                        </div>
                    </div>
                </Link>
            ))}
        </div>
    );
}
